import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, Phone, Mail, Pencil, Trash2, X, BookUser, Loader2, StickyNote, History, Camera, HardHat, ChevronLeft, ChevronRight, Truck, Wrench, Upload, ImageOff, Download, Printer } from "lucide-react";
import { api, API } from "../../lib/api";

const STATUS_LABEL = { pending: "Da fare", in_progress: "In corso", completed: "Completata" };
const STATUS_CHIP = { pending: "bg-white border-ink/15 text-ink", in_progress: "bg-sky-50 border-sky-300 text-sky-900", completed: "bg-emerald-50 border-emerald-300 text-emerald-900" };

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

export default function InstallerRubrica({ onOpenTask }) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState(null);
  const [edit, setEdit] = useState(null);
  const [history, setHistory] = useState(null); // { client, tasks } | { loading: true, name }
  const [archive, setArchive] = useState(null); // { loading, name } | { client, categories, deliveries, ... }
  const [uploadTarget, setUploadTarget] = useState("");
  const [uploadCategory, setUploadCategory] = useState("reportage");
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { i } indice in flatPhotos | null
  const token = localStorage.getItem("ah_token") || "";
  const photoSrc = (p) => `${API}${(p.url || "").replace("/api", "")}?token=${token}`;

  // Elenco piatto di tutte le foto dell'archivio (nell'ordine mostrato) per scorrere avanti/indietro
  const flatPhotos = React.useMemo(() => {
    const out = [];
    for (const cat of (archive?.categories || [])) {
      for (const p of (cat.photos || [])) {
        out.push({
          id: p.id,
          src: `${API}${(p.url || "").replace("/api", "")}?token=${token}`,
          catLabel: cat.label,
          date: p.task_date ? new Date(p.task_date + "T00:00:00").toLocaleDateString("it-IT") : "",
          worker: `${p.worker_name || ""}${p.uploaded_by_admin ? " (admin)" : ""}`.trim(),
          comment: p.comment || "",
        });
      }
    }
    return out;
  }, [archive, token]);

  // Navigazione da tastiera nel visualizzatore (← → Esc)
  useEffect(() => {
    if (!lightbox) return undefined;
    const n = flatPhotos.length;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight" && n) setLightbox((lb) => ({ i: (lb.i + 1) % n }));
      else if (e.key === "ArrowLeft" && n) setLightbox((lb) => ({ i: (lb.i - 1 + n) % n }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, flatPhotos.length]);

  const downloadPhoto = async (ph) => {
    try {
      const res = await fetch(ph.src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foto_${ph.catLabel}_${ph.date || ph.id}`.replace(/[^\w.-]+/g, "_") + ".jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare la foto");
    }
  };

  const printPhoto = (ph) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const cap = `${ph.catLabel} · ${ph.date || ""}${ph.worker ? " · " + ph.worker : ""}`.replace(/</g, "&lt;");
    w.document.write(`<html><head><title>Foto cliente</title></head><body style="margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:Helvetica,Arial,sans-serif"><img src="${ph.src}" style="max-width:100%;max-height:92vh;object-fit:contain" onload="setTimeout(function(){window.print()},150)"/><div style="padding:10px;color:#333;font-size:13px">${cap}</div></body></html>`);
    w.document.close();
  };

  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/installers/admin/clients", { params: { q } });
        setClients(data);
      } catch {
        toast.error("Errore caricamento rubrica");
        setClients([]);
      }
    }, q ? 250 : 0);
    return () => clearTimeout(h);
  }, [q]);

  async function openHistory(c) {
    setHistory({ loading: true, name: c.name });
    try {
      const { data } = await api.get(`/installers/admin/clients/${c.id}/history`);
      setHistory(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento storico");
      setHistory(null);
    }
  }

  async function openArchive(c) {
    setArchive({ loading: true, name: c.name, id: c.id });
    try {
      const { data } = await api.get(`/installers/admin/clients/${c.id}/photos`);
      setArchive({ ...data, id: c.id });
      setUploadTarget(data.deliveries?.[0]?.id || "");
      setUploadCategory(data.photo_categories?.[0]?.value || "reportage");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento archivio foto");
      setArchive(null);
    }
  }

  async function reloadArchive(clientId) {
    try {
      const { data } = await api.get(`/installers/admin/clients/${clientId}/photos`);
      setArchive((prev) => ({ ...data, id: clientId, _keepTarget: prev }));
    } catch { /* noop */ }
  }

  async function handleArchiveUpload(files) {
    if (!files || files.length === 0) return;
    if (!uploadTarget) { toast.error("Seleziona prima la consegna a cui agganciare le foto"); return; }
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", uploadCategory);
        fd.append("task_id", uploadTarget);
        await api.post("/installers/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Errore caricamento ${file.name}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} foto caricate — ora visibili agli operai su quella consegna`);
      await reloadArchive(archive.id);
    }
  }

  async function deleteArchivePhoto(p) {
    if (!window.confirm("Eliminare questa foto? L'operazione è definitiva.")) return;
    try {
      await api.delete(`/installers/photos/${p.id}`);
      toast.success("Foto eliminata");
      await reloadArchive(archive.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione foto");
    }
  }

  async function saveEdit(e) {
    e.preventDefault();
    try {
      await api.patch(`/installers/admin/clients/${edit.id}`, {
        name: edit.name, address: edit.address, phone: edit.phone, email: edit.email, delivery_notes: edit.delivery_notes,
      });
      toast.success("Cliente aggiornato");
      setEdit(null);
      const { data } = await api.get("/installers/admin/clients", { params: { q } });
      setClients(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento cliente");
    }
  }

  async function deleteClient(c) {
    if (!window.confirm(`Eliminare "${c.name}" dalla rubrica? Le consegne esistenti non vengono toccate.`)) return;
    try {
      await api.delete(`/installers/admin/clients/${c.id}`);
      toast.success("Cliente eliminato dalla rubrica");
      setClients((list) => list.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  return (
    <div className="space-y-4" data-testid="installers-rubrica">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            placeholder="Cerca cliente (nominativo, indirizzo, telefono, note)..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-ink/15 bg-white pl-9 pr-3 py-2.5 rounded-sm text-sm"
            data-testid="rubrica-search"
          />
        </div>
        {clients && <span className="text-[11px] text-slate1">{clients.length} client{clients.length === 1 ? "e" : "i"} · clicca su una scheda per lo storico consegne e assistenze</span>}
      </div>

      {!clients ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="rubrica-empty">
          <BookUser size={26} className="mx-auto mb-2 text-slate-300" />
          {q ? "Nessun cliente trovato" : "La rubrica si popola automaticamente quando crei le consegne"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {clients.map((c) => (
            <div
              key={c.id}
              className="bg-white border border-ink/10 rounded-sm p-4 cursor-pointer hover:border-ink/30 hover:shadow-sm transition-[border-color,box-shadow]"
              onClick={() => openHistory(c)}
              data-testid={`rubrica-client-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display text-ink text-lg leading-tight flex items-center gap-1.5">{c.name}</div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); openArchive(c); }} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Archivio foto del cliente" data-testid={`rubrica-photos-${c.id}`}><Camera size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); openHistory(c); }} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Storico consegne e assistenze" data-testid={`rubrica-history-${c.id}`}><History size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); setEdit({ id: c.id, name: c.name || "", address: c.address || "", phone: c.phone || "", email: c.email || "", delivery_notes: c.delivery_notes || "" }); }} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica" data-testid={`rubrica-edit-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteClient(c); }} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm" title="Elimina" data-testid={`rubrica-del-${c.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                {c.address && <div className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-400 shrink-0" /> <span className="truncate">{c.address}</span></div>}
                {c.phone && <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 hover:underline"><Phone size={12} className="text-slate-400 shrink-0" /> {c.phone}</a>}
                {c.email && <div className="flex items-center gap-1.5"><Mail size={12} className="text-slate-400 shrink-0" /> <span className="truncate">{c.email}</span></div>}
                {c.delivery_notes && (
                  <div className="flex items-start gap-1.5 text-[12px] text-amber-900 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1.5 mt-1.5" data-testid={`rubrica-notes-${c.id}`}>
                    <StickyNote size={12} className="shrink-0 mt-0.5 text-amber-600" /> {c.delivery_notes}
                  </div>
                )}
              </div>
              {(c.delivery_count > 0 || c.assistance_count > 0) && (
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  {c.delivery_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wide uppercase font-semibold border border-ink/15 bg-ink/[0.03] text-ink rounded px-1.5 py-0.5" data-testid={`rubrica-deliveries-count-${c.id}`}>
                      <Truck size={11} /> {c.delivery_count} consegn{c.delivery_count === 1 ? "a" : "e"}
                    </span>
                  )}
                  {c.assistance_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wide uppercase font-semibold border border-violet-300 bg-violet-50 text-violet-900 rounded px-1.5 py-0.5" data-testid={`rubrica-assistances-count-${c.id}`}>
                      <Wrench size={11} /> {c.assistance_count} assistenz{c.assistance_count === 1 ? "a" : "e"}
                    </span>
                  )}
                  {c.photo_count > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); openArchive(c); }} className="inline-flex items-center gap-1 text-[10px] tracking-wide uppercase font-semibold border border-sky-300 bg-sky-50 text-sky-900 rounded px-1.5 py-0.5 hover:bg-sky-100 transition-colors" title="Apri l'archivio foto" data-testid={`rubrica-photos-count-${c.id}`}>
                      <Camera size={11} /> {c.photo_count} foto
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {history && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="rubrica-history-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setHistory(null); }}>
          <div className="bg-cream w-[min(94vw,640px)] max-h-[86vh] flex flex-col border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Storico consegne e assistenze</div>
                <div className="font-display text-lg text-ink leading-tight">{history.loading ? history.name : history.client?.name}</div>
              </div>
              <button onClick={() => setHistory(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="rubrica-history-close"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {history.loading ? (
                <div className="flex justify-center py-10 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
              ) : history.tasks.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400" data-testid="rubrica-history-empty">Nessuna consegna o assistenza registrata per questo cliente</div>
              ) : (
                <>
                  <div className="text-[11px] text-slate1 px-1" data-testid="rubrica-history-count">
                    {(() => {
                      const na = history.tasks.filter((t) => t.task_type === "assistance").length;
                      const nd = history.tasks.length - na;
                      const parts = [];
                      if (nd > 0) parts.push(`${nd} consegn${nd === 1 ? "a" : "e"}`);
                      if (na > 0) parts.push(`${na} assistenz${na === 1 ? "a" : "e"}`);
                      return parts.join(" · ");
                    })()} · clicca per aprire il dettaglio
                  </div>
                  {history.tasks.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { if (onOpenTask) { setHistory(null); onOpenTask(t); } }}
                      className="w-full text-left bg-white border border-ink/10 rounded-sm p-3 hover:border-ink/30 transition-[border-color] flex items-center gap-3"
                      data-testid={`rubrica-history-task-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-ink">{fmtDate(t.scheduled_date)}</span>
                          {t.scheduled_time && <span className="text-[11px] text-slate1">ore {t.scheduled_time}</span>}
                          <span className={`text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${STATUS_CHIP[t.status] || STATUS_CHIP.pending}`} data-testid={`rubrica-history-status-${t.id}`}>
                            {STATUS_LABEL[t.status] || t.status}
                          </span>
                          {t.task_type === "assistance" && (
                            <span className="text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-violet-50 border-violet-300 text-violet-900" data-testid={`rubrica-history-assist-${t.id}`}>Assistenza</span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-slate-600">
                          {(t.workers || []).length > 0 && (
                            <span className="inline-flex items-center gap-1"><HardHat size={11} className="text-slate-400" /> {t.workers.map((w) => w.name).join(", ")}</span>
                          )}
                          {t.photo_count > 0 && <span className="inline-flex items-center gap-1"><Camera size={11} className="text-slate-400" /> {t.photo_count} foto</span>}
                          {t.client_delivery_notes && <span className="inline-flex items-center gap-1 text-amber-800"><StickyNote size={11} className="text-amber-500" /> <span className="truncate max-w-[220px]">{t.client_delivery_notes}</span></span>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 shrink-0" />
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {archive && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="rubrica-archive-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setArchive(null); }}>
          <div className="bg-cream w-[min(96vw,880px)] max-h-[90vh] flex flex-col border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Archivio foto cliente</div>
                <div className="font-display text-lg text-ink leading-tight">{archive.loading ? archive.name : archive.client?.name}</div>
              </div>
              <button onClick={() => setArchive(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="rubrica-archive-close"><X size={18} /></button>
            </div>

            <div className="overflow-y-auto p-4 space-y-5">
              {archive.loading ? (
                <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
              ) : (
                <>
                  {/* Upload */}
                  <div className="bg-white border border-ink/10 rounded-sm p-4" data-testid="rubrica-archive-upload">
                    <div className="text-[11px] tracking-[0.14em] uppercase text-ink font-semibold mb-2 inline-flex items-center gap-1.5"><Upload size={13} /> Aggiungi foto</div>
                    {(archive.deliveries || []).length === 0 ? (
                      <div className="text-[12px] text-slate1">Questo cliente non ha ancora consegne o assistenze: crea prima una consegna per poter agganciare le foto.</div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] tracking-[0.16em] uppercase text-slate1 block mb-1">Consegna / assistenza</label>
                            <select value={uploadTarget} onChange={(e) => setUploadTarget(e.target.value)} className="w-full border border-ink/20 px-2.5 py-2 rounded-sm bg-white text-sm" data-testid="rubrica-archive-target">
                              {archive.deliveries.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.task_type === "assistance" ? "Assistenza" : "Consegna"} · {d.scheduled_date ? new Date(d.scheduled_date + "T00:00:00").toLocaleDateString("it-IT") : "senza data"} · {d.title || "—"}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] tracking-[0.16em] uppercase text-slate1 block mb-1">Categoria</label>
                            <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} className="w-full border border-ink/20 px-2.5 py-2 rounded-sm bg-white text-sm" data-testid="rubrica-archive-category">
                              {(archive.photo_categories || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <label className={`mt-3 flex items-center justify-center gap-2 border border-dashed border-ink/25 rounded-sm py-3 text-[12px] cursor-pointer hover:bg-cream2 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="rubrica-archive-file-label">
                          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                          {uploading ? "Caricamento in corso..." : "Scegli le foto da caricare (diventano visibili agli operai sulla consegna scelta)"}
                          <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e) => { handleArchiveUpload(e.target.files); e.target.value = ""; }} data-testid="rubrica-archive-file-input" />
                        </label>
                      </>
                    )}
                  </div>

                  {/* Galleria per categoria */}
                  {(archive.categories || []).length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-400" data-testid="rubrica-archive-empty">
                      <ImageOff size={26} className="mx-auto mb-2 text-slate-300" />
                      Nessuna foto in archivio per questo cliente
                    </div>
                  ) : (
                    archive.categories.map((cat) => (
                      <div key={cat.category} data-testid={`rubrica-archive-cat-${cat.category}`}>
                        <div className="text-[11px] tracking-[0.14em] uppercase text-ink font-semibold mb-2 flex items-center gap-2">
                          {cat.label} <span className="text-slate1 font-normal normal-case tracking-normal">· {cat.count} foto</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {cat.photos.map((p) => (
                            <figure key={p.id} className="bg-white border border-ink/10 rounded-sm overflow-hidden group" data-testid={`rubrica-archive-photo-${p.id}`}>
                              <div className="relative aspect-[4/3] bg-cream2 cursor-zoom-in" onClick={() => setLightbox({ i: Math.max(0, flatPhotos.findIndex((x) => x.id === p.id)) })}>
                                <img src={photoSrc(p)} alt={cat.label} loading="lazy" className="w-full h-full object-cover" />
                                <button onClick={(e) => { e.stopPropagation(); deleteArchivePhoto(p); }} className="absolute top-1 right-1 p-1 bg-white/85 text-rose-600 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" title="Elimina foto" data-testid={`rubrica-archive-del-${p.id}`}><Trash2 size={13} /></button>
                              </div>
                              <figcaption className="p-2 text-[11px] text-slate-600 space-y-0.5">
                                <div className="flex items-center gap-1 text-ink font-medium">
                                  <Truck size={10} className="text-slate-400 shrink-0" />
                                  {p.task_date ? new Date(p.task_date + "T00:00:00").toLocaleDateString("it-IT") : "—"}
                                </div>
                                <div className="flex items-center gap-1 truncate">
                                  <HardHat size={10} className="text-slate-400 shrink-0" />
                                  <span className="truncate">{p.worker_name || "—"}{p.uploaded_by_admin ? " (admin)" : ""}</span>
                                </div>
                                {p.comment && (
                                  <div className="flex items-start gap-1 text-amber-800">
                                    <StickyNote size={10} className="text-amber-500 shrink-0 mt-0.5" />
                                    <span className="line-clamp-2">{p.comment}</span>
                                  </div>
                                )}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {lightbox && flatPhotos[lightbox.i] && (() => {
        const ph = flatPhotos[lightbox.i];
        const multi = flatPhotos.length > 1;
        return (
          <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-ink/90 p-4" data-testid="rubrica-lightbox" onClick={() => setLightbox(null)}>
            <div className="absolute top-0 inset-x-0 flex items-center justify-between gap-2 p-3 text-cream" onClick={(e) => e.stopPropagation()}>
              <div className="text-[12px] tracking-wider uppercase opacity-90" data-testid="rubrica-lightbox-counter">{lightbox.i + 1} / {flatPhotos.length} · {ph.catLabel}</div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => downloadPhoto(ph)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-white/10 hover:bg-white/20 text-[11px] tracking-wider uppercase font-bold transition-colors" data-testid="rubrica-lightbox-download"><Download size={14} /> <span className="hidden sm:inline">Scarica</span></button>
                <button onClick={() => printPhoto(ph)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-white/10 hover:bg-white/20 text-[11px] tracking-wider uppercase font-bold transition-colors" data-testid="rubrica-lightbox-print"><Printer size={14} /> <span className="hidden sm:inline">Stampa</span></button>
                <button onClick={() => setLightbox(null)} className="p-2 hover:bg-white/10 rounded-sm transition-colors" data-testid="rubrica-lightbox-close"><X size={20} /></button>
              </div>
            </div>
            {multi && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ i: (lb.i - 1 + flatPhotos.length) % flatPhotos.length })); }} className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 hover:bg-white/25 text-cream transition-colors" title="Foto precedente" data-testid="rubrica-lightbox-prev"><ChevronLeft size={26} /></button>
            )}
            <div className="max-w-[92vw] max-h-[82vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <img src={ph.src} alt={ph.catLabel} className="max-w-full max-h-[74vh] object-contain rounded-sm" data-testid="rubrica-lightbox-img" />
              <div className="text-cream text-[12px] text-center flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5">
                {ph.date && <span>{ph.date}</span>}
                {ph.worker && <span>· {ph.worker}</span>}
                {ph.comment && <span className="italic opacity-80">· “{ph.comment}”</span>}
              </div>
            </div>
            {multi && (
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ i: (lb.i + 1) % flatPhotos.length })); }} className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/10 hover:bg-white/25 text-cream transition-colors" title="Foto successiva" data-testid="rubrica-lightbox-next"><ChevronRight size={26} /></button>
            )}
          </div>
        );
      })()}

      {edit && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="rubrica-edit-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setEdit(null); }}>
          <div className="bg-cream w-[min(94vw,460px)] border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between">
              <div className="font-display text-lg text-ink">Modifica cliente</div>
              <button onClick={() => setEdit(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="rubrica-edit-close"><X size={18} /></button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-3">
              <input required placeholder="Nominativo *" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="rubrica-edit-name" />
              <input placeholder="Indirizzo" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="rubrica-edit-address" />
              <input placeholder="Telefono" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="rubrica-edit-phone" />
              <input placeholder="Email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white" data-testid="rubrica-edit-email" />
              <textarea placeholder="Note di consegna (piano, ascensore, accessi...)" rows={3} value={edit.delivery_notes} onChange={(e) => setEdit({ ...edit, delivery_notes: e.target.value })} className="w-full border border-ink/20 px-3 py-2.5 rounded-sm bg-white text-sm" data-testid="rubrica-edit-notes" />
              <button type="submit" className="w-full bg-ink text-cream py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="rubrica-edit-save">Salva</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
